import React from 'react'
import { Button, ButtonGroup } from '@blueprintjs/core'
import Next from './assets/Next'
import Prev from './assets/Prev'

const CELL_COUNT = 7

function pagingCells(n: number, pos: number) {
  // Consider an array of pages with length `n`. Let `p` be cursor position.
  //  [1, 2, 3, ..., n-1, n]
  //
  // Requirements:
  // - In all cases we want to keep `1` and `n` visible.
  // - We cant render more than CELL_COUNT items.
  // - If the cells exceed CELL_COUNT, insert `...` wherever appropriate.
  const offset = n - pos
  const pivot = ~~(CELL_COUNT / 2)

  const cells: {
    ellipsis?: boolean
    nr: number
  }[] = []

  if (n > CELL_COUNT) {
    // Fill in first and last positions
    cells[0] = { nr: 1 }
    cells[1] = { nr: 2 }
    cells[CELL_COUNT - 2] = { nr: n - 1 }
    cells[CELL_COUNT - 1] = { nr: n }

    if (pos <= pivot) {
      // last ellipse is enabled and the rest of the array is filled
      cells[CELL_COUNT - 2].ellipsis = true
      for (let i = 2; i < CELL_COUNT - 2; i++) {
        cells[i] = { nr: i + 1 }
      }
    } else if (offset < pivot) {
      // a ellipsis is enabled and the later part of array is filled
      cells[1].ellipsis = true
      for (let i = 2; i < CELL_COUNT - 2; i++) {
        cells[i] = { nr: n - CELL_COUNT + i + 1 }
      }
    } else {
      // both a and b ellipsis are enabled
      cells[1].ellipsis = true
      cells[CELL_COUNT - 2].ellipsis = true

      // Current selected is put in centre
      cells[pivot] = { nr: pos }
      // Fill next and prev to mid point
      // CELL_COUNT - 5 := n{MID, FIRST, SECOND, LAST, SECONDLAST}
      for (let i = 1; i < CELL_COUNT - 5; i++) {
        cells[pivot + i] = { nr: pos + i }
        cells[pivot - i] = { nr: pos - i }
      }
    }
  } else {
    for (let i = 0; i < n; i++) {
      cells[i] = { nr: i + 1, ellipsis: false }
    }
  }
  return cells
}

export function Pagination({
  current,
  totalPages,
  onChange,
}: {
  current: number
  totalPages: number
  onChange: (page: number) => void
}) {
  // Renders a Pagination Button Group, inserting ellipsis based on cursor.
  // ┌───┬───┬───┬───┬───┐
  // │ < │ 1 │ 2 │ 3 │ > │
  // └───┴───┴───┴───┴───┘
  // ┌───┬───┬───┬───┬───┬───┬───┬───┬───┐
  // │ < │ 1 │ 2 │ 3 │ 4 │...│ 9 │10 │ > │
  // └───┴───┴───┴───┴───┴───┴───┴───┴───┘
  // ┌───┬───┬───┬───┬───┬───┬───┬───┬───┐
  // │ < │ 1 │...│ 4 │ 5 │ 6 │...│10 │ > │
  // └───┴───┴───┴───┴───┴───┴───┴───┴───┘

  const PrevPage = (
    <Button
      minimal
      className="text-blue-200"
      disabled={current === 1}
      onClick={() => onChange(current - 1)}
      outlined
    >
      <Prev />
    </Button>
  )

  const NextPage = (
    <Button
      minimal
      className="text-blue-200"
      disabled={current >= totalPages}
      onClick={() => onChange(current + 1)}
      outlined
    >
      <Next />
    </Button>
  )

  return (
    <ButtonGroup className="pagination">
      {PrevPage}
      {pagingCells(totalPages, current).map(({ nr, ellipsis }) => (
        <Button
          minimal
          className="text-12px text-blue-200"
          style={{ color: 'var(--blue-200)' }}
          text={!ellipsis && nr}
          icon={ellipsis && 'more'}
          disabled={ellipsis}
          key={nr}
          active={nr === current}
          outlined
          onClick={() => onChange(nr)}
        />
      ))}
      {NextPage}
    </ButtonGroup>
  )
}

export default Pagination
